import { api } from './api'

export interface QueuedRequest {
  id: string
  url: string
  method: 'POST' | 'PUT'
  body: unknown
  headers: Record<string, string>
  createdAt: number
  retryCount: number
  status: 'pending' | 'syncing' | 'failed'
}

type SyncCallback = (success: boolean, error?: Error) => void

class OfflineQueue {
  private dbName = 'RailSahayakOfflineQueue'
  private storeName = 'requests'
  private db: IDBDatabase | null = null
  private isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
  private syncing = false
  private syncCallbacks: SyncCallback[] = []
  private pendingListeners: Array<(count: number) => void> = []

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleOnline())
      window.addEventListener('offline', () => this.handleOffline())
    }
  }

  private async initDb(): Promise<IDBDatabase> {
    if (this.db) return this.db

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' })
          store.createIndex('status', 'status', { unique: false })
          store.createIndex('createdAt', 'createdAt', { unique: false })
        }
      }
    })
  }

  async enqueue(url: string, method: 'POST' | 'PUT', body: unknown): Promise<void> {
    const db = await this.initDb()
    const request = await this.getAuthHeaders()

    const queuedRequest: QueuedRequest = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      url,
      method,
      body,
      headers: request,
      createdAt: Date.now(),
      retryCount: 0,
      status: 'pending'
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const addRequest = store.add(queuedRequest)

      addRequest.onerror = () => reject(addRequest.error)
      addRequest.onsuccess = () => {
        this.notifyPendingListeners()
        resolve()
      }
    })
  }

  async getQueue(): Promise<QueuedRequest[]> {
    const db = await this.initDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.getAll()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  async getPendingCount(): Promise<number> {
    const queue = await this.getQueue()
    return queue.filter((item) => item.status === 'pending').length
  }

  async sync(): Promise<void> {
    if (this.syncing || !this.isOnline) return
    this.syncing = true

    try {
      const queue = await this.getQueue()
      const pending = queue.filter((item) => item.status === 'pending')

      for (const request of pending) {
        await this.syncRequest(request)
      }

      const failed = queue.filter((item) => item.status === 'failed')
      if (failed.length === 0) {
        this.syncCallbacks.forEach((cb) => cb(true))
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      this.syncCallbacks.forEach((cb) => cb(false, err))
    } finally {
      this.syncing = false
    }
  }

  private async syncRequest(request: QueuedRequest): Promise<void> {
    try {
      await this.updateRequestStatus(request.id, 'syncing')

      const apiMethod = request.method === 'POST' ? api.post : api.put
      await apiMethod.call(api, request.url, request.body)

      await this.removeRequest(request.id)
      this.notifyPendingListeners()
    } catch (error) {
      const newRetryCount = request.retryCount + 1
      const maxRetries = 3

      if (newRetryCount < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, newRetryCount), 10000)
        setTimeout(() => {
          this.updateRequestStatus(request.id, 'pending', newRetryCount).catch(console.error)
        }, delay)
      } else {
        await this.updateRequestStatus(request.id, 'failed', newRetryCount)
      }
    }
  }

  private async updateRequestStatus(id: string, status: QueuedRequest['status'], retryCount?: number): Promise<void> {
    const db = await this.initDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const getRequest = store.get(id)

      getRequest.onsuccess = () => {
        const item = getRequest.result
        if (!item) return reject(new Error('Request not found'))

        item.status = status
        if (retryCount !== undefined) item.retryCount = retryCount

        const updateRequest = store.put(item)
        updateRequest.onerror = () => reject(updateRequest.error)
        updateRequest.onsuccess = () => resolve()
      }
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  private async removeRequest(id: string): Promise<void> {
    const db = await this.initDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.delete(id)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async clear(): Promise<void> {
    const db = await this.initDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.clear()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.notifyPendingListeners()
        resolve()
      }
    })
  }

  onSyncComplete(callback: SyncCallback): void {
    this.syncCallbacks.push(callback)
  }

  removeListener(callback: SyncCallback): void {
    this.syncCallbacks = this.syncCallbacks.filter((cb) => cb !== callback)
  }

  addPendingListener(callback: (count: number) => void): () => void {
    this.pendingListeners.push(callback)
    return () => {
      this.pendingListeners = this.pendingListeners.filter((cb) => cb !== callback)
    }
  }

  private notifyPendingListeners(): void {
    this.getPendingCount().then((count) => {
      this.pendingListeners.forEach((cb) => cb(count))
    })
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = await api.getAccessToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private handleOnline(): void {
    this.isOnline = true
    this.sync().catch(console.error)
  }

  private handleOffline(): void {
    this.isOnline = false
  }

  isSyncing(): boolean {
    return this.syncing
  }

  getIsOnline(): boolean {
    return this.isOnline
  }
}

export const offlineQueue = new OfflineQueue()
